import crypto from 'crypto';
import { FormValues, validPlatformVariants } from '../types';
import { templates } from '../templates';

export const defaultFormValues: FormValues = {
  template_version: 'v2',
  name: 'b2500',
  friendly_name: 'B2500',
  poll_interval_seconds: 5,
  log_level: 'INFO',
  mqtt: {
    enabled: true,
    topic: 'b2500',
    broker: 'mqttbroker.local',
    port: 1883,
    username: '',
    password: '',
    discovery: false,
  },
  wifi: {
    ssid: 'MyWifi',
    password: 'MyPassword',
  },
  board: 'esp32dev',
  variant: 'auto',
  flash_size: '4MB',
  idf_platform_version: '',
  enable_auto_restart: true,
  auto_restart: {
    restart_after_error_count: 8,
  },
  enable_cellquery: false,
  enable_timer_query: true,
  enable_cmd30: false,
  enable_esp_temperature: false,
  esp_temperature: {
    variant: 'internal',
  },
  enable_powermeter: false,
  enable_experimental_commands: false,
  enable_detailed_cell_sensors: false,
  publish_unchanged_sensor_values: true,
  enable_hexdump: false,
  enable_set_wifi: false,
  set_wifi: {
    ssid: 'MyWifi',
    password: 'MyPassword',
  },
  enable_set_mqtt: false,
  enable_reset_mqtt: false,
  powermeter: {
    tx_pin: 'GPIO6',
    rx_pin: 'GPIO7',
    baud_rate: 9600,
    stop_bits: 1,
  },
  enable_enforce_dod: false,
  enable_powerzero: false,
  powerzero: {
    grid_power_topic: 'tibber-esp/sensor/power/state',
    limit_cmd_topic: 'openDTU/XXXXXXXXXXXX/cmd/limit_persistent_relative',
    limit_state_topic: 'openDTU/XXXXXXXXXXXX/state/limit_relative',
  },
  enable_manual_ip: false,
  manual_ip: {
    ip: '192.168.1.100',
    gateway: '192.168.1.1',
    subnet: '255.255.255.0',
    dns: '192.168.1.1',
  },
  enable_web_server: true,
  web_server: {
    port: 80,
  },
  enable_ota: true,
  ota: {
    password: 'my_ota_password',
    enable_unprotected_writes: false,
  },
  enable_fallback_hotspot: true,
  fallback_hotspot: {
    ssid: 'ESPHome-b2500',
    enable_captive_portal: true,
  },
  native_api: {
    enabled: false,
  },
  enable_dio_flash_mode: false,
  use_legacy_entity_names: true,
  storages: [{ name: 'B2500', version: 2, mac_address: '00:00:00:00:00:00' }],
};

export const mergeDeep = (target: any, source: any) => {
  for (const key of Object.keys(source)) {
    if (source[key] instanceof Object) {
      if (!target[key]) {
        Object.assign(target, { [key]: {} });
      }
      mergeDeep(target[key], source[key]);
    } else {
      if (target[key] === undefined) {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }
  return target;
};

export const getAllSecrets = (debouncedFormValues: FormValues) => {
  const secrets = [];
  if (debouncedFormValues.mqtt.password) {
    secrets.push(debouncedFormValues.mqtt.password);
  }
  if (debouncedFormValues.wifi.password) {
    secrets.push(debouncedFormValues.wifi.password);
  }
  if (debouncedFormValues.set_wifi.password) {
    secrets.push(debouncedFormValues.set_wifi.password);
  }
  if (debouncedFormValues.ota.password) {
    secrets.push(debouncedFormValues.ota.password);
  }
  for (const storage of debouncedFormValues.storages) {
    if (storage.mac_address) {
      secrets.push(storage.mac_address);
      secrets.push(storage.mac_address.replace(/:/g, ''));
    }
    if (storage.id) {
      secrets.push(storage.id);
    }
  }
  return secrets;
};

export const redactSecrets = (config: FormValues) => {
  const redactedConfig = JSON.parse(JSON.stringify(config));
  redactedConfig.mqtt.password = '***';
  redactedConfig.wifi.password = '***';
  redactedConfig.set_wifi.password = '***';
  redactedConfig.ota.password = '***';
  for (const storage of redactedConfig.storages) {
    storage.mac_address = '***';
    if (storage.id) {
      storage.id = '***';
    }
  }
  return redactedConfig;
};

// The maximum number of BLE connections depends on the framework used.
// ESPHome's esp32_ble_tracker module defines `max_connections()` which
// returns 9 for ESP-IDF and 3 for Arduino. Since all templates in this
// project use ESP-IDF by default, we allow up to 9 B2500 devices.
export const getMaxBleDevices = (): number => 9;

/**
 * Colons only, matching scripts/validateConfig.js and ESPHome's
 * `cv.mac_address`, which splits on ":" and requires six parts. Kept in step by
 * index.test.ts, which reads the build's pattern out of validateConfig.js.
 */
export const MAC_ADDRESS = /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/;

/**
 * A PlatformIO board ID. Mirrors `board` in scripts/validateConfig.js, which
 * constrains it so the redacted failure log can print it verbatim - it is one
 * of the first things you want when someone reports a build that did not
 * compile. Kept in step by index.test.ts.
 */
export const BOARD = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

/**
 * Normalises a config that came from storage rather than from the form.
 *
 * The MAC field formats typed input to colons, but a JSON file and a restored
 * localStorage blob can hold anything - including a dash-separated address that
 * an older build of this page accepted. Rewriting beats rejecting: it repairs
 * the stored value instead of leaving the user to retype it.
 *
 * Shapes are handled explicitly rather than left to the caller's try/catch, so
 * a malformed stored config degrades to "no storages" instead of throwing
 * further up.
 */
export const normalizeImportedConfig = (config: FormValues): FormValues => ({
  ...config,
  storages: (Array.isArray(config.storages) ? config.storages : [])
    .filter((storage) => storage && typeof storage === 'object')
    .map((storage) => ({
      ...storage,
      mac_address: String(storage.mac_address ?? '').replace(/-/g, ':'),
    })),
});

export const validateConfig = (config: FormValues) => {
  const template = templates[config.template_version];
  const errors = [];
  if (!isPlatformVersionValid(config.idf_platform_version)) {
    errors.push('ESP-IDF platform version is invalid');
  }
  if (!BOARD.test(config.board)) {
    errors.push('Board is invalid');
  }
  if (!validPlatformVariants.includes(config.variant)) {
    errors.push('Variant is invalid');
  }
  if (config.storages.length === 0) {
    errors.push('At least one storage is required');
  }
  for (const storage of config.storages) {
    if (storage.name.trim() === '') {
      errors.push('Storage name is required');
    }
    if (
      template.capabilities.requiresStorageVersion &&
      (storage.version < 1 || storage.version > 2)
    ) {
      errors.push('Storage version is invalid');
    }
    if (storage.mac_address.trim() === '') {
      errors.push('Storage MAC address is required');
    }
    if (!MAC_ADDRESS.test(storage.mac_address)) {
      errors.push('Storage MAC address is invalid');
    }
    if (
      template.capabilities.requiresStorageId &&
      !/^[0-9A-Fa-f]{24}$/.test(storage.id ?? '')
    ) {
      errors.push('Storage ID is required');
    }
  }

  let nameInvalid =
    config.name.length > 31 || !/^[a-z0-9-]+$/.test(config.name);
  if (nameInvalid) {
    errors.push('Name is invalid');
  }
  if (config.friendly_name.trim() === '') {
    errors.push('Friendly name is required');
  }
  if (config.friendly_name.length > 63) {
    errors.push('Friendly name is too long');
  }
  if (config.wifi.ssid.trim() === '') {
    errors.push('WiFi SSID is required');
  }
  if (config.wifi.password.trim() === '') {
    errors.push('WiFi password is required');
  }
  if (!config.mqtt.enabled && !template.capabilities.canDisableMQTT) {
    errors.push(
      `MQTT cannot be disabled for the configuration version '${template.name}'`
    );
  }
  if (config.mqtt.enabled || !template.capabilities.canDisableMQTT) {
    if (config.mqtt.topic.trim() === '') {
      errors.push('MQTT topic is required');
    }
    if (config.mqtt.broker.trim() === '') {
      errors.push('MQTT broker is required');
    }
    if (config.mqtt.port <= 0 || config.mqtt.port > 65535) {
      errors.push('MQTT port is invalid');
    }
  }
  if (
    template.capabilities.hasAutoRestart &&
    config.auto_restart.restart_after_error_count < 0
  ) {
    errors.push('Auto restart count is invalid');
  }
  if (config.enable_powermeter) {
    if (config.powermeter.baud_rate < 0) {
      errors.push('Powermeter baud rate is invalid');
    }
    if (
      config.powermeter.stop_bits !== 1 &&
      config.powermeter.stop_bits !== 2
    ) {
      errors.push('Powermeter stop bits is invalid');
    }
    if (config.powermeter.tx_pin.trim() === '') {
      errors.push('Powermeter TX pin is required');
    }
    if (config.powermeter.rx_pin.trim() === '') {
      errors.push('Powermeter RX pin is required');
    }
  }
  if (config.enable_manual_ip) {
    if (config.manual_ip.ip.trim() === '') {
      errors.push('Manual IP address is required');
    }
    if (config.manual_ip.gateway.trim() === '') {
      errors.push('Manual gateway is required');
    }
    if (config.manual_ip.subnet.trim() === '') {
      errors.push('Manual subnet is required');
    }
    if (config.manual_ip.dns.trim() === '') {
      errors.push('Manual DNS is required');
    }
  }
  if (config.enable_web_server) {
    if (config.web_server.port <= 0 || config.web_server.port > 65535) {
      errors.push('Web server port is invalid');
    }
  }
  if (config.enable_ota) {
    if (config.ota.password.trim() === '') {
      errors.push('OTA password is required');
    }
    if (config.ota.enable_unprotected_writes) {
      errors.push(
        'Unprotected writes is not supported by this tool. See https://github.com/esphome/esphome/pull/5535 for more information and build the image manually.'
      );
    }
  }
  if (config.enable_fallback_hotspot) {
    if (config.fallback_hotspot.ssid.trim() === '') {
      errors.push('Fallback hotspot SSID is required');
    }
  }
  if (template.capabilities.hasStorage && config.enable_set_wifi) {
    if (config.set_wifi.ssid.trim() === '') {
      errors.push('Set WiFi SSID is required');
    }
    if (config.set_wifi.password.trim() === '') {
      errors.push('Set WiFi password is required');
    }
  }
  if (template.capabilities.hasPowerZeroScript && config.enable_powerzero) {
    if (config.powerzero.grid_power_topic.trim() === '') {
      errors.push('Powerzero grid power topic is required');
    }
    if (config.powerzero.limit_cmd_topic.trim() === '') {
      errors.push('Powerzero limit command topic is required');
    }
    if (config.powerzero.limit_state_topic.trim() === '') {
      errors.push('Powerzero limit state topic is required');
    }
  }
  return errors;
};

export const newIssueLink = ({
  config,
  build,
}: {
  config: FormValues;
  build?: string;
}) => {
  const redactedConfig = redactSecrets(config);
  const body = `<!-- Please describe the issue you are experiencing and provide as much context as possible. -->\n
**Describe the issue**\n
<!-- Please describe the issue you are experiencing and provide as much context as possible. -->\n
**Steps to reproduce**\n
<!-- If applicable, provide steps to reproduce the issue. -->\n
**Expected behavior**\n
<!-- What should happen? -->\n
**Actual behavior**\n
<!-- What happens instead? -->\n
**Additional context**\n
<!-- Add any other context about the problem here. -->\n
**Screenshots**\n
<!-- If applicable, add screenshots to help explain your problem. -->\n
**Device information**\n
- ESP Board: [e.g. ESP32-C3]\n
- B2500 Brand: [e.g. Marstek]\n
- B2500 Version: [e.g. v1, v2, v3]\n
${build ? `- Build: [${build}]\n` : ''}
**Configuration**\n
\`\`\`json\n${JSON.stringify(redactedConfig, null, 2)}\n\`\`\`\n
`;
  return `https://github.com/tomquist/esphome-b2500/issues/new?body=${encodeURIComponent(body)}`;
};

/**
 * Mirrors `idf_platform_version` in scripts/validateConfig.js. Kept in step by
 * utils/index.test.ts, which reads that file: a form that accepts more than the
 * build does turns into a five-minute build and an opaque failure.
 */
export const isPlatformVersionValid = (value: string | undefined) =>
  !value ||
  /^[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}(-[0-9A-Za-z]{1,16})?$/.test(value);

export const generateRandomIdentifier = () => {
  const adjectives = [
    'tiny',
    'fluffy',
    'happy',
    'sad',
    'angry',
    'brave',
    'quick',
    'lazy',
    'bright',
    'dark',
    'funky',
    'wobbly',
    'jumpy',
    'sparkly',
    'grumpy',
    'cheerful',
    'sneaky',
    'silly',
    'noisy',
    'sleepy',
    'bouncy',
    'zany',
    'quirky',
    'clumsy',
    'jazzy',
    'wiggly',
    'perky',
    'giggly',
    'bizarre',
    'nutty',
  ];

  const animals = [
    'cat',
    'dog',
    'rabbit',
    'lion',
    'tiger',
    'bear',
    'fox',
    'wolf',
    'mouse',
    'horse',
    'unicorn',
    'dragon',
    'penguin',
    'flamingo',
    'octopus',
    'giraffe',
    'koala',
    'panda',
    'squirrel',
    'hedgehog',
    'dolphin',
    'kangaroo',
    'lemur',
    'meerkat',
    'narwhal',
    'platypus',
    'quokka',
    'sloth',
    'toucan',
  ];

  // Not a secret - it shows up in the workflow's run-name and in the run log,
  // and the firmware object it names is sealed to the requesting page's key
  // anyway. The entropy is so that two builds never land on the same object and
  // so the bucket is not walkable; Math.random() is too weak for either. The
  // words are there to make a build recognisable in the Actions list.
  // toString() rather than a Buffer read method: the bundle resolves `crypto`
  // to crypto-browserify, whose Buffer is the `buffer` polyfill.
  const pick = <T>(items: T[]): T =>
    items[parseInt(crypto.randomBytes(4).toString('hex'), 16) % items.length];

  const randomAdjective1 = pick(adjectives);
  const randomAdjective2 = pick(adjectives);
  const randomAnimal = pick(animals);
  // 96 bits, lowercase hex so the result still matches the ^[a-z0-9-]{1,64}$
  // the build workflow enforces.
  const randomSuffix = crypto.randomBytes(12).toString('hex');

  return `${randomAdjective1}-${randomAdjective2}-${randomAnimal}-${randomSuffix}`;
};
